import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import spaceManagementService from "../../services/host-apiServices/spaceManagementService";
import billingService from "../../services/billingService";
import logger from "../../utils/logger";
import HeroSearchSection from "../../components/HeroSearchSection";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import QRCode from "react-qr-code";
import { generateSpaceName } from "../../utils/spaceNamingUtils";
import SEO from "../../components/SEO/SEO";

// Type definitions
interface CategoryCardProps {
  title: string;
  description: string;
  image: string;
  buttonText: string;
  sectionId?: string;
}

interface ListingCardProps {
  image: string;
  title: string;
  location: string;
  rating: number;
  price: string;
  available: boolean;
  category: string;
  subcategory?: string;
  featured?: boolean;
}

interface ListingSectionProps {
  title: string;
  listings: ListingCardProps[];
  viewAllText: string;
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// Hero Section Component
const HeroSection = () => {
  return (
    <HeroSearchSection
      title="The Future of Work, Stay & Play"
      subtitle="Discover flexible workspaces, unique accommodations, and curated experiences"
      showImage={true}
      animateHeroImage={false}
      animateText={false}
      animateBackground={false}
    />
  );
};

// Category Card Component
const CategoryCard: React.FC<CategoryCardProps & { sectionId?: string; onNavigate?: (sectionId: string) => void }> = ({
  title,
  description,
  image,
  buttonText,
  sectionId,
  onNavigate,
}) => {
  const handleClick = () => {
    if (sectionId && onNavigate) {
      onNavigate(sectionId);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-[1.02]"
    >
      <div className="h-48 sm:h-56 md:h-64 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <img src={image || "/placeholder.svg"} alt={title} className="w-full h-full object-contain" />
      </div>
      <div className="p-4 sm:p-5 md:p-6 text-center">
        <h3 className="text-lg sm:text-xl font-bold mb-2">{title}</h3>
        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-2">{description}</p>
        <button
          onClick={e => {
            e.stopPropagation(); // Prevent double navigation
            handleClick();
          }}
          className="w-full bg-blue-600 text-white py-2 sm:py-2.5 rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base font-medium"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

// Browse by Category Section
const BrowseByCategorySection = ({
  categories: rawCategories,
  loading,
  workSpaces,
  staySpaces,
  playSpaces,
}: {
  categories: any[];
  loading: boolean;
  workSpaces: any[];
  staySpaces: any[];
  playSpaces: any[];
}) => {
  // Map category names to showcase SVG files
  const getShowcaseImage = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    if (name.includes("work") || name.includes("office") || name.includes("coworking")) {
      return "/assets/img/showcase1.svg";
    } else if (name.includes("stay") || name.includes("accommodation") || name.includes("hotel")) {
      return "/assets/img/showcase2.svg";
    } else if (name.includes("play") || name.includes("experience") || name.includes("event")) {
      return "/assets/img/showcase3.svg";
    }
    // Default fallback based on index if category name doesn't match
    return "/assets/img/showcase1.svg";
  };

  // Filter and sort categories to ensure Work, Stay, Play order
  const getCategoryOrder = (categoryName: string): number => {
    const name = categoryName.toLowerCase();
    if (name.includes("work") || name.includes("office") || name.includes("coworking")) {
      return 1; // Work = showcase1
    } else if (name.includes("stay") || name.includes("accommodation") || name.includes("hotel")) {
      return 2; // Stay = showcase2
    } else if (name.includes("play") || name.includes("experience") || name.includes("event")) {
      return 3; // Play = showcase3
    }
    return 99; // Others go last
  };

  const workStayPlayCategories = rawCategories
    .filter((category: any) => {
      const categoryName = (category.name || "").toLowerCase();
      const isWork = categoryName.includes("work") || categoryName.includes("office");
      const isStay = categoryName.includes("stay") || categoryName.includes("accommodation");
      const isPlay = categoryName.includes("play") || categoryName.includes("experience");

      if (isWork) return workSpaces.length > 0;
      if (isStay) return staySpaces.length > 0;
      if (isPlay) return playSpaces.length > 0;

      return false;
    })
    .sort((a: any, b: any) => {
      const orderA = getCategoryOrder(a.name || "");
      const orderB = getCategoryOrder(b.name || "");
      return orderA - orderB;
    });

  // Transform API data to component format with showcase images
  const categories = workStayPlayCategories.map(category => {
    const categoryName = category.name || "";
    const normalizedName = categoryName.toLowerCase();

    // Determine section ID based on category name
    let sectionId = "work-section";
    if (normalizedName.includes("stay") || normalizedName.includes("accommodation")) {
      sectionId = "stay-section";
    } else if (normalizedName.includes("play") || normalizedName.includes("experience")) {
      sectionId = "play-section";
    } else if (normalizedName.includes("work") || normalizedName.includes("office")) {
      sectionId = "work-section";
    }

    return {
      title: categoryName,
      description: category.description || "Discover amazing spaces",
      image: getShowcaseImage(categoryName),
      buttonText: `Explore ${categoryName}`,
      sectionId: sectionId,
    };
  });

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      // Try to find the section by category name
      const categoryName = sectionId.replace("-section", "");
      const categoryElement = document.getElementById(`${categoryName}-section`);
      if (categoryElement) {
        categoryElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Browse by Category</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            Whether you're looking to work, stay or play we have options for every need
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 sm:py-12">
            <LoadingSpinner
              loading={true}
              message="Loading categories..."
              size="lg"
              className="text-blue-600"
              containerClassName=""
              center={true}
              padding="py-8"
            />
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 ${
              categories.length === 1
                ? "sm:grid-cols-1 max-w-sm mx-auto"
                : categories.length === 2
                  ? "sm:grid-cols-2 max-w-2xl mx-auto"
                  : "sm:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {categories.map((category, index) => (
              <CategoryCard key={index} {...category} onNavigate={scrollToSection} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Figma Extracted Assets by dimensions & categories
const FIGMA_WORK_IMAGES = [
  "/figma-assets/bcfa3b2857b153c8e3c731f8e25103cbea0a0866.png",
  "/figma-assets/f6ab174955da55c7b03389440a128077e8f8f878.png",
  "/figma-assets/db1d2ecf43f82fe51bc568ee2475befbdebbc69c.png",
  "/figma-assets/04711f54e073a37c500ef8be39ce066716de6673.png",
  "/figma-assets/c17e1e07225e82fb6cf49096025a3206b244b93c.png",
  "/figma-assets/1295eccfa5108b3a7b0bca65f43775fd00e10238.png",
  "/figma-assets/c410fdb08b18f77db6ebbbcb94e231142fadbc78.png",
  "/figma-assets/bcc31ad7c7b32e801bf1e2e7e0d4c58585fd16e3.png",
  "/figma-assets/fed8f19929328288342cec9ddb51993cd3db2f10.png",
  "/figma-assets/0b44c5cb71a0c43fd5f64608f18151bbac788866.png",
];

const FIGMA_STAY_IMAGES = [
  "/figma-assets/43382c88d2178fc1a76556f53232672f3a5f9794.jpg",
  "/figma-assets/6bfecae266d0780f0abbaa7522a71eb33b9c8e49.jpg",
  "/figma-assets/83c49b18e50735c5db878ca3030619c037b46228.jpg",
  "/figma-assets/372330cb9ff42fa3003e56876a34ea81a6ab2aec.jpg",
  "/figma-assets/cc67f508dd73cd416cdc5b72bd3468d28877e675.jpg",
];

const FIGMA_PLAY_IMAGES = [
  "/figma-assets/0b5054db818f705ae8f29a06d986dd8f0fcbdc25.jpg",
  "/figma-assets/c84a70aa7ceb412d180e4571e82e430063e35e43.jpg",
  "/figma-assets/f4809b18e797b09d3695c6daa0da48fb94141e28.jpg",
  "/figma-assets/d5a15405fae2bc74af62f79e91fe715080ce529c.jpg",
  "/figma-assets/6036d7bce630d5b1b50060dfc013d0848f6f1288.jpg",
];

// Helper to extract image URLs from space object
const extractImageUrls = (space: any): string[] => {
  let extracted: string[] = [];
  if (space?.media?.images && Array.isArray(space.media.images) && space.media.images.length > 0) {
    extracted = space.media.images.map((img: any) => (typeof img === "string" ? img : img.url)).filter(Boolean);
  } else if (space?.images && Array.isArray(space.images) && space.images.length > 0) {
    extracted = space.images.map((img: any) => (typeof img === "string" ? img : img.url || img)).filter(Boolean);
  } else if (space?.image) {
    extracted = [space.image];
  }

  if (extracted.length > 0) {
    return extracted;
  }

  // Fallback to high-resolution Figma design assets based on category or ID
  const catName = (space?.category?.name || space?.category || "").toLowerCase();
  const idHash = Math.abs((space?.id || 0) % 5);

  if (catName.includes("stay") || catName.includes("accommodation") || catName.includes("hotel")) {
    return [FIGMA_STAY_IMAGES[idHash % FIGMA_STAY_IMAGES.length]];
  } else if (catName.includes("play") || catName.includes("experience") || catName.includes("event")) {
    return [FIGMA_PLAY_IMAGES[idHash % FIGMA_PLAY_IMAGES.length]];
  }
  return [FIGMA_WORK_IMAGES[idHash % FIGMA_WORK_IMAGES.length]];
};

// Airbnb-style image carousel component - memoized to prevent unnecessary re-renders
const SpaceImageCarousel = React.memo(
  ({
    space,
    className = "",
    autoCycle = true,
    isHovered = false,
  }: {
    space: any;
    className?: string;
    autoCycle?: boolean;
    isHovered?: boolean;
  }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageErrors, setImageErrors] = useState(new Set<number>());

    // Create a stable string key from image URLs to prevent unnecessary recalculations
    const imageUrlsKey = extractImageUrls(space).join("|");

    // Get images array - memoized based on the actual URL content, not object references
    const images = useMemo(() => {
      return imageUrlsKey.split("|");
    }, [imageUrlsKey]);

    // Auto-cycle through images only when hovered (consistent with other carousels)
    useEffect(() => {
      if (!autoCycle || !isHovered || images.length <= 1) return;

      // Generate random interval between 5-7 seconds (5000-7000ms)
      const randomInterval = Math.random() * 2000 + 5000; // 5000 + (0-2000) = 5000-7000ms

      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
      }, randomInterval);

      return () => clearInterval(interval);
    }, [images.length, autoCycle, isHovered]);

    // Don't show carousel if only one image
    if (images.length <= 1) {
      const imgSrc = images[0];
      const isPlaceholder = imgSrc === "/img/placeholder-space.jpg";
      return (
        <img
          src={imgSrc}
          alt={space.name || space.title}
          className={`w-full h-full object-cover rounded-lg ${className}`}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            // Prevent infinite loop: don't set placeholder if already showing placeholder
            if (!isPlaceholder && e.currentTarget.src !== "/img/placeholder-space.jpg") {
              e.currentTarget.src = "/img/placeholder-space.jpg";
            }
          }}
        />
      );
    }

    const handlePrevImage = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNextImage = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const handleImageError = (index: number) => {
      setImageErrors(prev => new Set([...prev, index]));
    };

    const goToImage = (index: number, e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentImageIndex(index);
    };

    const currentImage = images[currentImageIndex];
    const hasError = imageErrors.has(currentImageIndex);

    return (
      <div className={`relative w-full h-full overflow-hidden rounded-lg ${className}`}>
        {/* Main Image */}
        <img
          src={hasError ? "/img/placeholder-space.jpg" : currentImage}
          alt={`${space.name || space.title} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
          onError={() => handleImageError(currentImageIndex)}
        />

        {/* Navigation Arrows - Show on hover */}
        {images.length > 1 && (
          <>
            <button
              onClick={e => {
                e.stopPropagation();
                handlePrevImage(e);
              }}
              className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4 text-gray-700" />
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                handleNextImage(e);
              }}
              className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4 text-gray-700" />
            </button>
          </>
        )}

        {/* Image Indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
            {images.map((_: string, index: number) => (
              <button
                key={index}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  goToImage(index, e);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentImageIndex ? "bg-white shadow-lg" : "bg-white/50 hover:bg-white/70"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md font-medium">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison based on actual image content, not object reference
    const prevUrls = extractImageUrls(prevProps.space).join("|");
    const nextUrls = extractImageUrls(nextProps.space).join("|");
    return (
      prevUrls === nextUrls &&
      prevProps.className === nextProps.className &&
      prevProps.autoCycle === nextProps.autoCycle &&
      prevProps.isHovered === nextProps.isHovered
    );
  }
);

// Space Image with Fallback Component (similar to SpaceCard)
const SpaceImageWithFallback = ({
  image,
  title,
  className = "",
}: {
  image: string | null;
  title: string;
  className?: string;
}) => {
  const [hasError, setHasError] = useState(false);

  if (!image || hasError) {
    return (
      <div
        className={`flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-100 ${className}`}
      >
        <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      </div>
    );
  }

  return <img src={image} alt={title} className={`object-cover ${className}`} onError={() => setHasError(true)} />;
};

// Listing Card Component
const ListingCard: React.FC<ListingCardProps & { space?: any }> = ({
  image: _image,
  title,
  location,
  rating: _rating,
  price,
  available,
  category,
  subcategory,
  featured,
  space,
}) => {
  const history = useHistory();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="bg-white rounded-lg sm:rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {space ? (
          <SpaceImageCarousel space={space} className="w-full h-40 sm:h-48" isHovered={isHovered} />
        ) : (
          <div className="w-full h-40 sm:h-48 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
            <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
        )}
        {available && !featured && (
          <span className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-green-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
            Available
          </span>
        )}
        <span className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-white text-gray-900 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold shadow-sm">
          {price}
        </span>
      </div>
      <div className="p-3 sm:p-4">
        <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">{category}</span>
          {subcategory && (
            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">{subcategory}</span>
          )}
        </div>
        <h3 className="font-bold text-sm sm:text-base mb-2 line-clamp-2">{title}</h3>
        <div className="flex items-center text-gray-600 text-xs sm:text-sm mb-3">
          <svg
            className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate">{location}</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => {
              if (space?.id) {
                // Pass the full space data via location state to avoid extra API call
                history.push({
                  pathname: `/space/${space.id}`,
                  state: { spaceData: space },
                });
              }
            }}
            className="flex-1 border border-gray-300 text-gray-700 py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-50 font-medium text-sm"
          >
            View Details
          </button>
          <button className="flex-1 bg-blue-600 text-white py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-700 font-medium text-sm">
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Featured Listings Section
const FeaturedListingsSection = ({
  categories: _rawCategories,
  workSpaces,
  staySpaces,
  playSpaces,
  loading,
}: {
  categories: any[];
  workSpaces: any[];
  staySpaces: any[];
  playSpaces: any[];
  loading: boolean;
}) => {
  // Transform API data to component format
  const transformSpaceData = (spaces: any[], isFeatured: boolean = false): ListingCardProps[] => {
    return spaces.map(space => {
      // Get the first instance for location and title info
      const firstInstance = space.instances?.[0];
      const buildingAddress = firstInstance?.location?.buildingAddress;

      const title = generateSpaceName(space, firstInstance);

      // Extract location from buildingAddress structure
      let location = "Location";
      if (firstInstance) {
        if (buildingAddress) {
          const building = buildingAddress;
          location = `${building.buildingName}, ${building.city}, ${building.country}`;
        } else if (firstInstance.location?.name) {
          location = firstInstance.location.name;
        }
      }

      // Extract pricing from ratePrice field - improved formatting
      let priceDisplay = "Contact for pricing";
      if (space.ratePrice && space.ratePrice > 0) {
        const currency = space.currency || "USD";
        // Format price with proper currency symbol and decimal places
        const formattedPrice = space.ratePrice.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        priceDisplay = `${currency} ${formattedPrice}/day`;
      }

      // Extract image from the API response structure - use media.images
      let imageUrl = null;

      // Check for images in the correct API structure: space.media.images
      if (space.media && space.media.images && Array.isArray(space.media.images) && space.media.images.length > 0) {
        // Get the first valid image URL
        const validImage = space.media.images.find((img: any) => img && typeof img === "string" && img.trim() !== "");
        if (validImage) {
          imageUrl = validImage;
        }
      }

      // Extract category and subcategory - ensure they're strings
      const category = typeof space.category === "string" ? space.category : space.category?.name || "Work";
      const subcategory =
        typeof space.subCategory === "string" ? space.subCategory : space.subCategory?.name || "Space";

      // Extract capacity and size info
      const _capacity = space.capacity || 0;
      const _size = space.size || "";

      // Determine availability based on status and available instances
      const isAvailable = space.status === "PUBLISHED" && space.availableInstancesCount > 0;

      const transformedSpace = {
        image: imageUrl, // This will be null if no image, triggering fallback
        title: title,
        location: location,
        rating: 4.5, // Default rating since not in the response
        price: priceDisplay,
        available: isAvailable,
        category: category,
        subcategory: subcategory,
        featured: isFeatured || space.featured || false,
        space: space, // Keep reference to original space object for navigation
      };

      // Debug logging to check data extraction
      logger.log("Space data extraction:", {
        id: space.id,
        title,
        imageUrl,
        category,
        subcategory,
        priceDisplay,
        location,
        hasMedia: !!space.media,
        hasImages: space.media?.images?.length || 0,
      });

      return transformedSpace;
    });
  };

  // Create category listings from the pre-fetched spaces
  const categoryListings = {
    Work: transformSpaceData(workSpaces),
    Stay: transformSpaceData(staySpaces),
    Play: transformSpaceData(playSpaces),
  };

  const ListingSection: React.FC<ListingSectionProps> = ({ title, listings, viewAllText }) => {
    if (listings.length === 0) return null;

    return (
      <div className="mb-8 sm:mb-12 md:mb-16">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-2 sm:gap-0">
          <div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold">{title}</h3>
          </div>
          <button
            className="text-blue-600 hover:text-blue-700 cursor-pointer text-sm sm:text-base font-medium self-start sm:self-auto"
            onClick={() => (window.location.href = "/spaces")}
          >
            {viewAllText}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {listings.slice(0, 4).map((listing, index) => (
            <ListingCard key={index} {...listing} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <section className="py-8 sm:py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Featured Listings</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-4xl mx-auto">
            Discover unique experiences curated by local hosts and connect with like-minded professionals everywhere.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 sm:py-12">
            <LoadingSpinner
              loading={true}
              message="Loading featured listings..."
              size="lg"
              className="text-blue-600"
              containerClassName=""
              center={true}
              padding="py-8"
            />
          </div>
        ) : (
          <>
            <div id="work-section">
              <ListingSection
                title="Work"
                listings={categoryListings["Work"] || []}
                viewAllText="View All Work Spaces"
              />
            </div>
            <div id="stay-section">
              <ListingSection
                title="Stay"
                listings={categoryListings["Stay"] || []}
                viewAllText="View All Stay Spaces"
              />
            </div>
            <div id="play-section">
              <ListingSection
                title="Play"
                listings={categoryListings["Play"] || []}
                viewAllText="View All Play Spaces"
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// Feature Card Component
const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="text-center p-4">
      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
        {icon}
      </div>
      <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{title}</h3>
      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

// Why SPACIA Section
const WhySpaciaSection = () => {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8v-4z"
          />
        </svg>
      ),
      title: "Flexible Access",
      description:
        "Book spaces and experiences on-demand with flexible cancellation policies and instant confirmation.",
    },
    {
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      title: "Curated Quality",
      description:
        "Every listing is personally verified and curated to ensure the highest standards of quality and service.",
    },
    {
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
      title: "One Platform",
      description:
        "Everything you need for work, stay, and play in one seamless platform with unified booking and payments.",
    },
  ];

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Why SPACIA?</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            We're reimagining how people work, travel, and connect across Africa
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Animated Counter Component
const AnimatedCounter: React.FC<{
  targetValue: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  duration?: number;
  useKFormat?: boolean;
}> = ({ targetValue, suffix = "", prefix = "", decimals = 0, duration = 3500, useKFormat = false }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasAnimated) return;

    const animateCounter = () => {
      const startTime = Date.now();
      const startValue = 0;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = startValue + (targetValue - startValue) * easeOutQuart;

        setCount(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(targetValue);
        }
      };

      requestAnimationFrame(animate);
    };

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounter();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [hasAnimated, targetValue, duration]);

  const formatNumber = (value: number): string => {
    if (useKFormat && value >= 1000) {
      const kValue = value / 1000;
      if (decimals > 0) {
        return kValue.toFixed(decimals) + "k";
      }
      return Math.floor(kValue) + "k";
    }
    if (decimals > 0) {
      return value.toFixed(decimals);
    }
    return Math.floor(value).toString();
  };

  return (
    <div ref={counterRef} className="text-2xl sm:text-3xl font-bold">
      {prefix}
      {formatNumber(count)}
      {suffix}
    </div>
  );
};

// CTA Section Component
const CTASection = () => {
  const history = useHistory();

  const handleBecomeHost = () => {
    history.push("/package-signup");
  };

  const handleLearnMore = () => {
    history.push("/corporate-page#faqSection");
  };

  return (
    <section className="bg-blue-600 text-white py-8 sm:py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
          Join Africa's fastest-growing work & travel community
        </h2>
        <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-blue-100 max-w-4xl mx-auto">
          Connect with thousands of professionals, discover unique spaces, and experience the best of African cities.
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8 sm:mb-12">
          <button
            onClick={handleBecomeHost}
            className="bg-white text-blue-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
          >
            Become a Host
          </button>
          <button
            onClick={handleLearnMore}
            className="border-2 border-white text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200"
          >
            Learn More
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          <div className="p-2">
            <AnimatedCounter targetValue={10000} suffix="+" useKFormat={true} />
            <div className="text-sm sm:text-base text-blue-200">Active Users</div>
          </div>
          <div className="p-2">
            <AnimatedCounter targetValue={500} suffix="+" />
            <div className="text-sm sm:text-base text-blue-200">Verified Spaces</div>
          </div>
          <div className="p-2">
            <AnimatedCounter targetValue={25} suffix="+" />
            <div className="text-sm sm:text-base text-blue-200">Cities</div>
          </div>
          <div className="p-2">
            <div className="text-2xl sm:text-3xl font-bold">4.8★</div>
            <div className="text-sm sm:text-base text-blue-200">Average Rating</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// QR Code Button Component
const QRCodeButton = () => {
  const [qrValue, setQrValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const qrUrl = `${window.location.origin}/qr-code?ref=landing-page`;
        setQrValue(qrUrl);
      } catch (error) {
        // Fallback to qr-code screen
        const fallbackUrl = `${window.location.origin}/qr-code`;
        setQrValue(fallbackUrl);
      }

      setIsLoading(false);
    };

    generateQRCode();
  }, []);

  if (isLoading) {
    return (
      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="group relative">
      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center group-hover:border-blue-300 group-hover:shadow-md transition-all duration-200">
        <QRCode value={qrValue} size={64} style={{ height: "auto", maxWidth: "100%", width: "100%" }} />
      </div>
      {/* Tooltip */}
      <div className="absolute -bottom-10 sm:-bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
        Scan to download app
        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
      </div>
    </div>
  );
};

// Mobile App Section
const MobileAppSection = ({ history: _history }: { history: any }) => {
  return (
    <section className="py-12 sm:py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-left order-2 lg:order-1">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 sm:mb-8">
              <span className="text-blue-600">ONE</span>
              <br />
              <span className="text-gray-900">Membership</span>
              <br />
              <span className="text-gray-900 italic">Multiple Locations</span>
            </h2>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 lg:gap-8 mt-8 sm:mt-12">
              {/* Text */}
              <div className="flex-shrink-0">
                <p className="text-gray-900 font-medium text-base sm:text-lg">
                  Join <span className="underline decoration-blue-600 decoration-2">us on</span>
                  <br />
                  <span className="underline decoration-blue-600 decoration-2">mobile!</span>
                </p>
              </div>

              {/* QR Code */}
              <div className="flex-shrink-0">
                <QRCodeButton />
              </div>

              {/* App Store Badges - Stacked Vertically */}
              <div className="flex-shrink-0">
                <div className="flex flex-col gap-2 sm:gap-3">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.lucid_array.spacia.individual&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:scale-105 transition-transform duration-200 -ml-2.5"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-14 sm:h-16 w-auto"
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/gh/app/spacia-work-stay-play/id6739746297"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:scale-105 transition-transform duration-200"
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on App Store"
                      className="h-10 sm:h-12 w-auto"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Phone Mockup */}
          <div className="flex justify-center lg:justify-end order-1 lg:order-2">
            <div className="relative">
              <img
                src={require("../../assets/img/sliding_exhibit.png")}
                alt="SPACIA Mobile App"
                className="w-64 sm:w-72 md:w-80 h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main SPACIA Landing Page Component
export default function LandingWeb() {
  const history = useHistory();
  const [categories, setCategories] = useState<{ name: string; description?: string; id: number }[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [workSpaces, setWorkSpaces] = useState<any[]>([]);
  const [staySpaces, setStaySpaces] = useState<any[]>([]);
  const [playSpaces, setPlaySpaces] = useState<any[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(true);
  const [videoShouldLoad, setVideoShouldLoad] = useState(false);

  // Load video only after page has fully loaded
  useEffect(() => {
    const handleLoad = () => {
      setVideoShouldLoad(true);
    };

    if (document.readyState === "complete") {
      // Page already loaded
      setVideoShouldLoad(true);
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  // Fetch categories and then fetch spaces for each category
  useEffect(() => {
    const fetchCategoriesAndSpaces = async () => {
      try {
        setCategoriesLoading(true);
        setSpacesLoading(true);

        // First, fetch categories
        const response = await billingService.getPublicServiceCategories();

        // Ensure we have an array, handle different response structures
        let categoriesData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            categoriesData = response.data;
          } else if (Array.isArray(response.data.content)) {
            categoriesData = response.data.content;
          } else if (Array.isArray(response.data.data)) {
            categoriesData = response.data.data;
          }
        }

        setCategories(categoriesData);
        logger.log("Service categories loaded:", categoriesData);

        // Now fetch spaces for each category - only 3 calls for Work, Stay, Play
        if (categoriesData.length > 0) {
          // Filter to only get Work, Stay, Play categories
          const workStayPlayCategories = categoriesData.filter((category: any) => {
            const categoryName = category.name.toLowerCase();
            return (
              categoryName.includes("work") ||
              categoryName.includes("stay") ||
              categoryName.includes("play") ||
              categoryName.includes("office") ||
              categoryName.includes("accommodation") ||
              categoryName.includes("experience")
            );
          });

          const spacePromises = workStayPlayCategories.map(async (category: any) => {
            try {
              // Only make ONE call per category - get featured spaces
              const response = await spaceManagementService.searchPublicSpacesUnauthenticated({
                categoryId: category.id,
                featured: true,
                status: "PUBLISHED",
                pageSize: 4,
                pageNumber: 0,
                "sort.sorted": true,
              });

              return {
                categoryName: category.name,
                categoryId: category.id,
                spaces: response.data?.content || [],
              };
            } catch (error) {
              logger.error(`Error fetching spaces for category ${category.name}:`, error);
              return {
                categoryName: category.name,
                categoryId: category.id,
                spaces: [],
              };
            }
          });

          const spaceResults = await Promise.all(spacePromises);

          // Map spaces to their respective categories
          spaceResults.forEach(result => {
            const categoryName = result.categoryName.toLowerCase();
            if (
              categoryName.includes("work") ||
              categoryName.includes("office") ||
              categoryName.includes("coworking")
            ) {
              setWorkSpaces(result.spaces);
            } else if (
              categoryName.includes("stay") ||
              categoryName.includes("accommodation") ||
              categoryName.includes("hotel")
            ) {
              setStaySpaces(result.spaces);
            } else if (
              categoryName.includes("play") ||
              categoryName.includes("experience") ||
              categoryName.includes("event")
            ) {
              setPlaySpaces(result.spaces);
            }
          });

          // Log the actual loaded spaces
          const loadedSpaces = {
            work:
              spaceResults.find((r: any) => {
                const name = (r.categoryName || "").toLowerCase();
                return name.includes("work") || name.includes("office");
              })?.spaces?.length || 0,
            stay:
              spaceResults.find((r: any) => {
                const name = (r.categoryName || "").toLowerCase();
                return name.includes("stay") || name.includes("accommodation");
              })?.spaces?.length || 0,
            play:
              spaceResults.find((r: any) => {
                const name = (r.categoryName || "").toLowerCase();
                return name.includes("play") || name.includes("experience");
              })?.spaces?.length || 0,
          };
          logger.log("Spaces loaded:", loadedSpaces);
        }
      } catch (error) {
        logger.error("Error fetching categories and spaces:", error);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
        setSpacesLoading(false);
      }
    };

    fetchCategoriesAndSpaces();
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Spacia - Work, Stay & Play Across Africa | Flexible Workspaces & Accommodations"
        description="Discover flexible workspaces, unique accommodations, and curated experiences across Africa. Book coworking spaces, hotels, and experiences on one platform. Join thousands of professionals using Spacia."
        keywords={[
          "spacia",
          "coworking spaces africa",
          "workspace booking",
          "hotel booking africa",
          "flexible workspace",
          "africa travel",
          "business accommodation",
          "work stay play",
        ]}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Spacia",
            description:
              "An innovative aggregator platform for workspaces, accommodations, and experiences across Africa",
            url: typeof window !== "undefined" ? window.location.origin : "",
            logo:
              typeof window !== "undefined"
                ? `${window.location.origin}/assets/img/spacia/web/spacia_logo_new.png`
                : "",
            sameAs: [
              "https://www.instagram.com/spaciaafrica/",
              "https://apps.apple.com/gh/app/spacia-work-stay-play/id6739746297",
              "https://play.google.com/store/apps/details?id=com.lucid_array.spacia.individual",
            ],
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "Customer Service",
              availableLanguage: "English",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Spacia",
            url: typeof window !== "undefined" ? window.location.origin : "",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate:
                  typeof window !== "undefined" ? `${window.location.origin}/spaces?search={search_term_string}` : "",
              },
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      <HeroSection />
      <BrowseByCategorySection
        categories={categories}
        loading={categoriesLoading}
        workSpaces={workSpaces}
        staySpaces={staySpaces}
        playSpaces={playSpaces}
      />
      <FeaturedListingsSection
        categories={categories}
        workSpaces={workSpaces}
        staySpaces={staySpaces}
        playSpaces={playSpaces}
        loading={spacesLoading}
      />
      <WhySpaciaSection />
      {/* Demo Video */}
      <section className="pt-6 sm:pt-10 md:pt-12 pb-24 sm:pb-20 md:pb-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-white rounded-3xl border border-gray-100 overflow-hidden">
            <div className="w-full bg-black">
              {videoShouldLoad && (
                <video
                  className="w-full h-auto"
                  src="/spaciavideo.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                />
              )}
            </div>
          </div>
        </div>
      </section>
      <CTASection />
      <MobileAppSection history={history} />
    </div>
  );
}
